import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listDisputes } from "@/services/disputes.service";
import { fmtMoney, fmtDateTime, statusColor } from "@/lib/format";
import { ArrowRight } from "lucide-react";
import DisputeDetail from "@/components/disputes/DisputeDetail";
import { usePolling } from "@/lib/usePolling";

const FILTERS = [["all", "All"], ["Open", "Open"], ["UnderReview", "Under review"], ["Resolved", "Resolved"], ["Cancelled", "Cancelled"]];

const AdminDisputes = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try { setDisputes(await listDisputes()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  usePolling(load);

  const filtered = useMemo(
    () => disputes.filter((d) => statusFilter === "all" || d.status === statusFilter),
    [disputes, statusFilter],
  );

  const openCount = disputes.filter((d) => ["Open", "UnderReview"].includes(d.status)).length;

  return (
    <div className="space-y-6" data-testid="admin-disputes-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Admin</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Disputes</h1>
        <p className="mt-2 text-sm text-muted-foreground">{openCount} awaiting review. Resolve to unblock escrow release.</p>
      </div>

      <div className="inline-flex items-center gap-1 bg-secondary/50 border border-border rounded-lg p-1 overflow-x-auto max-w-full">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setStatusFilter(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-md whitespace-nowrap transition-colors ${statusFilter === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`admin-dispute-filter-${k}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No disputes{statusFilter !== "all" ? ` with status ${statusFilter}` : ""}.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-muted-foreground text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Dispute ID</th>
                <th className="text-left px-4 py-3 font-semibold">Contract</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Milestone</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Filed by</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">Created</th>
                <th className="text-right px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((d) => (
                <tr key={d.dispute_id} className="hover:bg-secondary/30" data-testid={`admin-dispute-row-${d.dispute_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{d.dispute_id}</td>
                  <td className="px-4 py-3">
                    <Link to={`/contracts/${d.contract_id}`} className="font-medium hover:text-primary">{d.contract_title || "—"}</Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{d.milestone_title || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{d.opened_by_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-display font-semibold">{fmtMoney(d.amount)}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor(d.status)}`}>{d.status === "UnderReview" ? "Under review" : d.status}</span></td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{fmtDateTime(d.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setDetailId(d.dispute_id)} className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1" data-testid={`admin-view-dispute-${d.dispute_id}`}>
                      Review <ArrowRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId && <DisputeDetail disputeId={detailId} onClose={() => setDetailId(null)} onResolved={load} />}
    </div>
  );
};

export default AdminDisputes;
